import { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { patientService } from '../services/patientService'
import { immunisationService } from '../services/immunisationService'
import { Patient } from '../types/patient'
import { Immunisation } from '../types/immunisation'
import './ImmunisationFeeClaim.css'

export function ImmunisationFeeClaim() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const {
    data: patient,
    isLoading: loadingPatient,
    error: patientError,
  } = useQuery<Patient>({
    queryKey: ['patient', id],
    queryFn: () => patientService.getById(id!),
    enabled: !!id,
  })

  const {
    data: immunisationsResponse,
    isLoading: loadingImmunisations,
    error: immunisationsError,
  } = useQuery<{ items: Immunisation[]; totalCount: number; hasMore: boolean }>({
    queryKey: ['patient-immunisations', id, 'fee-claim'],
    queryFn: () => immunisationService.getPatientImmunisations(id!),
    enabled: !!id,
  })

  const isLoading = loadingPatient || loadingImmunisations
  const error = patientError || immunisationsError

  useEffect(() => {
    // Optionally auto-open print dialog once loaded
    if (!isLoading && !error) {
      const t = setTimeout(() => {
        window.print()
      }, 300)
      return () => clearTimeout(t)
    }
  }, [isLoading, error])

  if (!id) {
    return <div className="fee-claim-wrapper">Invalid patient.</div>
  }

  if (isLoading) {
    return <div className="fee-claim-wrapper">Preparing fee claim form...</div>
  }

  if (error || !patient || !immunisationsResponse) {
    return <div className="fee-claim-wrapper">Unable to load data for fee claim.</div>
  }

  const immunisations = immunisationsResponse.items || []

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  return (
    <div className="fee-claim-page">
      <div className="print-toolbar no-print">
        <button className="btn-secondary" onClick={() => navigate(-1)}>
          Back
        </button>
        <button className="btn-primary" onClick={() => window.print()}>
          Print
        </button>
      </div>

      <header className="fee-claim-header">
        <h1>Immunisation Fee Claim Form</h1>
        <div className="practice-details">
          <div>
            <strong>Practice Name:</strong> [Practice Name]
          </div>
          <div>
            <strong>Practice Number:</strong> [Practice Number]
          </div>
          <div>
            <strong>Date:</strong> {new Date().toLocaleDateString('en-GB')}
          </div>
        </div>
      </header>

      <section className="patient-details-section">
        <h2>Patient Details</h2>
        <div className="patient-details-grid">
          <div>
            <strong>Name:</strong> {patient.firstName} {patient.lastName}
          </div>
          <div>
            <strong>Date of Birth:</strong>{' '}
            {new Date(patient.dateOfBirth).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
            })}
          </div>
          <div>
            <strong>Patient Number:</strong> {patient.patientNumber || patient.id.slice(0, 8)}
          </div>
          {patient.ppsNumber && (
            <div>
              <strong>PPS Number:</strong> {patient.ppsNumber}
            </div>
          )}
          {patient.gmsNumber && (
            <div>
              <strong>GMS Number:</strong> {patient.gmsNumber}
            </div>
          )}
        </div>
      </section>

      <section className="immunisations-section">
        <h2>Immunisations Administered</h2>
        {immunisations.length === 0 ? (
          <p className="muted">No immunisations recorded.</p>
        ) : (
          <table className="fee-claim-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Vaccine Type</th>
                <th>Batch Number</th>
                <th>Manufacturer</th>
                <th>Dose</th>
                <th>Fee Code</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {immunisations.map((imm) => (
                <tr key={imm.immunisationId}>
                  <td>{formatDate(imm.date)}</td>
                  <td>{imm.type}</td>
                  <td>{imm.batchNumber}</td>
                  <td>{imm.manufacturer}</td>
                  <td>
                    {imm.dose.value} {imm.dose.unit}
                  </td>
                  <td>[Fee Code]</td>
                  <td>[Amount]</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={6} className="total-label">
                  <strong>Total Amount:</strong>
                </td>
                <td className="total-amount">
                  <strong>[Total]</strong>
                </td>
              </tr>
            </tfoot>
          </table>
        )}
      </section>

      <section className="signature-section">
        <div className="signature-block">
          <div>
            <strong>Practitioner Signature:</strong>
          </div>
          <div className="signature-line">_________________________</div>
          <div>
            <strong>Date:</strong> {new Date().toLocaleDateString('en-GB')}
          </div>
        </div>
      </section>

      <footer className="fee-claim-footer">
        <p>
          <small>
            This form is for fee claim submission. Please complete fee codes and amounts as per current
            GMS/state scheme guidelines.
          </small>
        </p>
      </footer>
    </div>
  )
}
